import React, {useEffect, useRef} from 'react';
import {IMaskInput} from "react-imask";
import {clearRequiredFieldError, resetRequiredFieldError} from "../../utils/project-utils.js";
import {useRequiredFieldError} from "../../hooks/use-required-field-error.js";

const InputForm = ({classNameWrapper, mask, id, name, maxLength, label, value, defaultValue, type, disabled, onBlur, onChange, onInput, readOnly, reference, inputRef: externalInputRef, span, autofocus, inputClass = "", labelClass = "", min, max, autocomplete = "on", required, invalid = false, onKeyDown, maskOptions, onFocus, onClick, placeholder = "", title, isClearable = false, integerOnly = false, ...props}) => {

    const inputRef = useRef(null);

    const hasInputValue = (inputValue) => inputValue !== null && inputValue !== undefined && inputValue !== "";
    const getCurrentValue = () => value ?? defaultValue ?? "";
    const hasCurrentValue = hasInputValue(getCurrentValue());
    const showClearButton = (isClearable) && !disabled && !readOnly && hasCurrentValue;

    const acceptsOnlyIntegers = type === "number" && integerOnly;
    const isRequired = required || (typeof label === "string" && label.trim().endsWith("*"));
    const hasRequiredBackground = classNameWrapper?.includes("bg-[#fdfdee]");
    const labelText = typeof label === "string" ? label.replace(/\s*\*\s*$/, "") : label;
    const hasRequiredValue = hasCurrentValue;
    const requiredFieldInvalid = useRequiredFieldError(inputRef, isRequired);
    const hasRequiredError = !disabled && !hasRequiredValue && (invalid || requiredFieldInvalid);
    const previousIsRequired = useRef(isRequired);

    const handleKeyDown = (event) => {
        if (acceptsOnlyIntegers && ["e", "E", "+", "-", ".", ","].includes(event.key)) event.preventDefault();
        onKeyDown?.(event);
    }

    const handlePaste = (event) => {
        if (acceptsOnlyIntegers) {
            const pasted = event.clipboardData?.getData("text") ?? "";
            const digits = pasted.replace(/\D/g, "");

            if (digits !== pasted) {
                event.preventDefault();

                if (inputRef.current) inputRef.current.value = digits;

                const pasteEvent = {
                    target: {id, name, value: digits},
                    currentTarget: {id, name, value: digits},
                };

                onChange?.(pasteEvent);
                onInput?.(pasteEvent);
                return;
            }
        }

        props.onPaste?.(event);
    }

    const handleChange = (event) => {
        if (acceptsOnlyIntegers) event.target.value = event.target.value.replace(/\D/g, "");
        onChange?.(event);
    }

    useEffect(() => {
        if (isRequired && hasCurrentValue) clearRequiredFieldError(inputRef.current);
    }, [value, defaultValue, isRequired]);

    useEffect(() => {
        const becameOptional = previousIsRequired.current && !isRequired;

        if (disabled || becameOptional) {
            resetRequiredFieldError(inputRef.current);
        }

        previousIsRequired.current = isRequired;
    }, [disabled, isRequired]);

    const setInputRef = (element) => {
        inputRef.current = element;

        if (typeof externalInputRef === "function") externalInputRef(element);
        else if (externalInputRef) externalInputRef.current = element;

        if (typeof reference === "function") {
            reference(element);
            return;
        }

        if (reference) reference.current = element;
    };

    const handleInput = (event) => {
        onInput?.(event);
    };

    const handleClear = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }

        const event = {
            target: {id, name, value: ""},
            currentTarget: {id, name, value: ""},
        };

        onChange?.(event);
        onInput?.(event);
    };

    const classNameInput = [
        "block w-full px-2.5 pb-1 pt-1 text-sm bg-transparent border-[1px] border-[#e5e7eb] rounded-md appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer h-[32px] transition-[border-color,border-width] duration-150 ease-out",
        showClearButton ? "pr-8" : "",
        hasRequiredBackground ? "!bg-[#fdfdee]" : "",
        inputClass,
        hasRequiredError ? "!border-[2px] !border-[#dc2626] focus:!border-[#dc2626]" : ""
    ].join(" ");

    return (
        <div className={["relative", classNameWrapper].join(" ")}>
            {
                mask &&
                <IMaskInput
                    {...maskOptions}
                    mask={mask}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    value={getCurrentValue()}
                    type={type}
                    className={classNameInput}
                    disabled={disabled}
                    onBlur={onBlur}
                    onKeyDown={handleKeyDown}
                    onAccept={(e, f, g) => {if(g) handleChange(g)}}
                    onInput={handleInput}
                    autoFocus={autofocus}
                    onFocus={onFocus}
                    required={isRequired}
                    data-required-field={isRequired || undefined}
                    aria-invalid={hasRequiredError}
                    inputRef={setInputRef}
                    {...props}
                />
            }

            {
                !mask &&
                <input
                    maxLength={maxLength}
                    id={id}
                    name={name}
                    placeholder={placeholder}
                    value={getCurrentValue()}
                    type={type}
                    className={classNameInput}
                    disabled={disabled}
                    onBlur={onBlur}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    readOnly={readOnly}
                    autoFocus={autofocus}
                    onFocus={onFocus}
                    ref={setInputRef}
                    min={min}
                    max={max}
                    inputMode={acceptsOnlyIntegers ? "numeric" : undefined}
                    autoComplete={autocomplete}
                    required={isRequired}
                    data-required-field={isRequired || undefined}
                    aria-invalid={hasRequiredError}
                    onClick={onClick}
                    title={title}
                />
            }

            { showClearButton && (
                <button
                    type="button" onClick={handleClear}
                    className="absolute right-0 top-0 z-20 flex h-[32px] w-8 items-center justify-center text-[#cccccc] hover:text-[#999999]"
                >
                    <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                        <path
                            fill="currentColor"
                            d="M14.348 14.849c-.469.469-1.229.469-1.697 0L10 12.197l-2.651 2.652c-.469.469-1.229.469-1.697 0s-.469-1.229 0-1.697L8.303 10 5.651 7.348c-.469-.469-.469-1.228 0-1.697s1.228-.469 1.697 0L10 8.303l2.651-2.652c.469-.469 1.228-.469 1.697 0s.469 1.228 0 1.697L11.697 10l2.651 2.651c.469.469.469 1.229 0 1.698z"
                        />
                    </svg>
                </button>
            )}

            <label htmlFor={id} style={{whiteSpace: "nowrap"}}
                   className={"cursor-text absolute text-sm px-1 text-gray-500 duration-300 transform " +
                       "-translate-y-4 scale-75 top-[5px] z-10 origin-[0]  peer-placeholder-shown:-top-[2px] " +
                       "peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-2 peer-focus:scale-75 " +
                       "peer-focus:-translate-y-4 left-2.5 peer-focus:top-[5px] " +
                       "peer-focus:px-1 max-w-full truncate " +
                       (disabled || readOnly ? "peer-placeholder-shown:bg-[#e9ecef] bg-disabled " : "bg-[linear-gradient(to_bottom,transparent_50%,#FFF_50%)] ") +
                       labelClass
                   }>
                {labelText}{isRequired ? <span className="ml-0.5">*</span> : null}
            </label>

            {span}
        </div>
    );
};

export default InputForm;